import React, { useEffect, useRef, useState } from 'react'

import { enrollmentHelperClient } from '@api/enrollmentClient'
import { getXCLEARCorrelationInfo } from '@api/utils'
import LookUpForm, {
  ApplicationIdLookupFormSubmission,
  BiographicsLookupFormSubmission,
  LookupFormFields,
} from '@components/pages/shared/LookUpForm'
import KtnCard from '@components/pages/ktn-lookup/KtnCard'
import { KPI } from '@utils/customEvents'
import PageContent from '@components/layout/PageContent'
import { Box } from '@clear/design-system'
import VerificationCodeForm, {
  ValidateVerificationCodeSubmitted,
} from '@components/VerificationCodeForm'
import {
  KTNDetails,
  OptKtndetailsSessionByApplicationIdRequest,
  OptKtndetailsSessionRequest,
} from '@clear-denver/tpc-openapi-typescript/build/v1/enrollment'
import { Modal } from '@components/shared/Modal'
import { PlanTypes, useEnrollmentContext } from '@contexts/EnrollmentContext'
import { useRouter } from 'next/router'
import CollectContactInfo, {
  ValidatedContactInfo,
} from '@components/SelectVerificationMethodForm'
import { useLookupFlow } from '@hooks/useLookupFlow'
import { handleScenario } from '@utils/resolver'
import { useFlag } from '@hooks/useFlag'
import { PageHeading } from '@components/layout/Heading'
import { useLookupPipe } from '@hooks/useLookupPipe'
import { resolveKtnSuccessCode } from '@hooks/useLookupPipe/lib/resolveSuccessCode'

type PageState = Partial<
  LookupFormFields & {
    otpSessionId?: string
  } & ValidatedContactInfo
>

type PageVariant = 'KTNFound' | 'SendOTP' | 'VerifyOTP' | ''

const PAGE_TITLE = 'Look Up KTN'

const DEFAULT_VALUES: PageState = {
  applicationId: undefined,
  countryOfBirth: undefined,
  dob: undefined,
  emailAddress: undefined,
  firstName: undefined,
  lastName: undefined,
  middleName: undefined,
  noMiddleName: undefined,
  otpSessionId: undefined,
  phoneNumber: undefined,
} as const

type LookupSubmissionOptions = {
  contact?: Pick<ValidatedContactInfo, 'emailAddress' | 'phoneNumber'>
  meta: {
    kpi: KPI
    xCLEARCorrelationInfo: string
  }
}

function KtnLookupPage() {
  const router = useRouter()

  const { setSelectedPlan } = useEnrollmentContext()
  const { getResolver } = useLookupFlow()
  const pipe = useLookupPipe()
  const flag = useFlag('updateLookupUx') ?? { enabled: undefined }
  const scenario = useRef<'MULTIPLE_MEMBERS' | undefined>()

  const [responseData, setResponseData] = useState<KTNDetails | null>(null)
  const [pageState, setPageState] = useState<Partial<PageState>>({
    ...DEFAULT_VALUES,
  })
  const [pageVariant, setPageVariant] = useState<PageVariant>('')
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    pipe.view()
  }, [pipe, router.isReady])

  const onBiographicsSearchSubmission = async (
    { type: _, ...data }: BiographicsLookupFormSubmission,
    { contact, meta }: LookupSubmissionOptions
  ) => {
    const info = contact ?? {}
    setPageState({ ...DEFAULT_VALUES, ...data, ...info })

    pipe.search({ status: 'begin', searchType: 'biographics' })

    try {
      const response = await enrollmentHelperClient.updatedKtndetailsLookup({
        xCLEARCorrelationInfo: meta.xCLEARCorrelationInfo,
        biographicsLookup: {
          ...data,
          ...info,
          dob: new Date(data.dob),
        },
      })

      if (!!response.otpSessionId && !response.needCommMethod) {
        pipe.search({
          status: 'success',
          code: 'OTP_SENT',
          hasOtpSessionId: true,
        })
        setPageState((prev) => ({
          ...prev,
          otpSessionId: response.otpSessionId,
        }))
        setPageVariant('VerifyOTP')
      } else if (response.needCommMethod) {
        pipe.search({
          status: 'success',
          code: 'NEED_COMM_METHOD',
          hasOtpSessionId: false,
          needCommMethod: true,
        })
        setPageVariant('SendOTP')
      }
    } catch (error: unknown) {
      handleScenario(getResolver('ktn', 'biographics'), error, (code) => {
        if (flag.enabled && code === 'MULTIPLE_MEMBERS') {
          pipe.search({
            status: 'success',
            code: 'DISAMBIGUATION',
            hasOtpSessionId: false,
            disambiguation: true,
          })
          scenario.current = code
          setPageVariant('SendOTP')
        } else {
          pipe.search({ status: 'failure', error, code })
          setShowErrorModal(true)
        }
      })
    }
  }

  const onApplicationIdSearchSubmission = async (
    { type: _, applicationId }: ApplicationIdLookupFormSubmission,
    { meta }: LookupSubmissionOptions
  ) => {
    setPageState({ ...DEFAULT_VALUES, applicationId })

    pipe.search({ status: 'begin', searchType: 'applicationId' })

    try {
      const response =
        await enrollmentHelperClient.updatedKtnLookupByApplicationId({
          xCLEARCorrelationInfo: meta.xCLEARCorrelationInfo,
          applicationId,
        })

      if (response?.otpSessionId) {
        pipe.search({
          status: 'success',
          code: 'OTP_SENT',
          hasOtpSessionId: true,
        })
        setPageState((prev) => ({
          ...prev,
          otpSessionId: response.otpSessionId,
        }))
        setPageVariant('VerifyOTP')
      }
    } catch (error: unknown) {
      handleScenario(getResolver('ktn', 'applicationId'), error, (code) => {
        pipe.search({ status: 'failure', error, code })
        setShowErrorModal(true)
      })
    }
  }

  async function onSendOTP(formData?: ValidatedContactInfo): Promise<void> {
    setIsLoading(true)
    const isCodeResent = formData === undefined
    const emailAddress = isCodeResent
      ? (pageState.emailAddress as string)
      : formData.emailAddress
    const phoneNumber = isCodeResent
      ? (pageState.phoneNumber as string)
      : formData.countryCode + '.' + formData.phoneNumber
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()

    if (scenario.current === 'MULTIPLE_MEMBERS') {
      scenario.current = undefined
      pipe.disambiguation({ status: 'begin' })
      const meta = { kpi: KPI.USER_INTERACTION, xCLEARCorrelationInfo }
      await onBiographicsSearchSubmission(
        {
          type: 'biographics',
          firstName: pageState.firstName,
          lastName: pageState.lastName,
          middleName: pageState.middleName,
          noMiddleName: pageState.noMiddleName,
          countryOfBirth: pageState.countryOfBirth,
          dob: pageState.dob,
        } as BiographicsLookupFormSubmission,
        {
          contact: { emailAddress, phoneNumber },
          meta,
        }
      )
      setIsLoading(false)
      return
    }

    pipe.sendOTP({ status: 'begin' })

    try {
      const response = await enrollmentHelperClient.otpKtndetails({
        otpForm: { emailAddress, phoneNumber },
        xCLEARCorrelationInfo,
      })

      pipe.sendOTP({ status: 'success' })
      setPageState((prev) => ({
        ...prev,
        phoneNumber,
        emailAddress,
        otpSessionId: response.otpSessionId,
      }))
      setPageVariant('VerifyOTP')
    } catch (error: unknown) {
      handleScenario(getResolver('ktn', 'sendOTP'), error, (code) => {
        pipe.sendOTP({ status: 'failure', error, code })
        setShowErrorModal(true)
      })
    }
    setIsLoading(false)
  }

  async function onBiographicsVerifyOTP(req: OptKtndetailsSessionRequest) {
    pipe.lookup({ status: 'begin' })
    try {
      const response = await enrollmentHelperClient.optKtndetailsSession(req)
      if (response) {
        pipe.lookup({
          status: 'success',
          code: resolveKtnSuccessCode(response),
        })
        /** known bug about events dropped when emitted too close together */
        setTimeout(() => pipe.conclude(), 3_000)
        setResponseData(response)
        setPageVariant('KTNFound')
      }
    } catch (error: unknown) {
      handleScenario(getResolver('ktn', 'biographicsLookup'), error, (code) => {
        pipe.lookup({ status: 'failure', error, code })
        setShowErrorModal(true)
      })
    }
  }

  async function onApplicationIdVerifyOTP(
    req: OptKtndetailsSessionByApplicationIdRequest
  ) {
    pipe.lookup({ status: 'begin' })
    try {
      const response =
        await enrollmentHelperClient.optKtndetailsSessionByApplicationId(req)
      if (response) {
        pipe.lookup({
          status: 'success',
          code: resolveKtnSuccessCode(response),
        })
        /** known bug about events dropped when emitted too close together */
        setTimeout(() => pipe.conclude(), 3_000)
        setResponseData(response)
        setPageVariant('KTNFound')
      }
    } catch (error: unknown) {
      handleScenario(
        getResolver('ktn', 'applicationIdLookup'),
        error,
        (code) => {
          pipe.lookup({ status: 'failure', error, code })
          setShowErrorModal(true)
        }
      )
    }
  }

  async function onVerifyOTP(formData: ValidateVerificationCodeSubmitted) {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const otpSessionId = pageState?.otpSessionId as string

    pipe.verifyOTP({ status: 'begin' })

    await enrollmentHelperClient
      .optAppstatusChallenge({
        otpSessionId,
        otpCode: formData.verificationCode,
        xCLEARCorrelationInfo,
      })
      .then(async () => {
        pipe.verifyOTP({ status: 'success' })
        if (pageState.applicationId) {
          await onApplicationIdVerifyOTP({
            otpSessionId,
            applicationId: pageState.applicationId,
            xCLEARCorrelationInfo,
          })
        } else {
          await onBiographicsVerifyOTP({
            otpSessionId,
            xCLEARCorrelationInfo,
            biographicsLookup: {
              ...(pageState as Required<PageState>),
              ...formData,
            },
          })
        }
      })
      .catch((error: unknown) => {
        handleScenario(getResolver('ktn', 'verifyOTP'), error, (code) => {
          pipe.verifyOTP({ status: 'failure', error, code })
          setShowErrorModal(true)
        })
      })

    setIsLoading(false)
  }

  const onClickEnroll = () => {
    setSelectedPlan(PlanTypes.ENROLL)
    router.push('/enroll/document-check')
  }

  return (
    <>
      {pageVariant === 'VerifyOTP' && !!pageState?.otpSessionId ? (
        <PageContent>
          <PageHeading>{PAGE_TITLE}</PageHeading>
          <Box
            width={['95%', '572px']}
            marginLeft="auto"
            marginRight="auto"
            border="1px solid #CCCED1"
            borderRadius="8px"
            paddingX={['20px', '50px']}
            paddingY={['20px', '40px']}
            backgroundColor="neutralsWhite"
          >
            <VerificationCodeForm
              isLoading={isLoading}
              otpFailureMessage={undefined}
              contactInfo={{
                otpSessionId: pageState.otpSessionId,
                emailAddress: pageState.emailAddress,
                phoneNumber: pageState.phoneNumber,
              }}
              onSubmitCode={onVerifyOTP}
              onResendCode={() => onSendOTP()}
            />
          </Box>
        </PageContent>
      ) : pageVariant === 'SendOTP' ? (
        <CollectContactInfo
          onSubmit={onSendOTP}
          variant={flag.enabled ? 'lookup' : 'default'}
          pageTitle={PAGE_TITLE}
        />
      ) : pageVariant === 'KTNFound' && responseData ? (
        <KtnCard data={responseData} />
      ) : (
        <LookUpForm
          isSearchableByApplicationId={flag.enabled}
          formTitle="What is my KTN? | TSA&nbsp;PreCheck® Known Traveler Number Look Up"
          formSubtitle="If you are a TSA&nbsp;PreCheck® member, you can look up your known traveler number online. Find your KTN here."
          pageTitle={PAGE_TITLE}
          onSubmit={async (data) => {
            setIsLoading(true)
            const meta = {
              kpi: KPI.USER_INTERACTION,
              xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
            }

            if (data.type === 'app') {
              await onApplicationIdSearchSubmission(data, { meta })
            } else {
              await onBiographicsSearchSubmission(data, { meta })
            }
            setIsLoading(false)
          }}
        />
      )}
      <Modal
        showModal={showErrorModal}
        setShowModal={setShowErrorModal}
        header="Information Not Found"
        body="We couldn't find a record based on the information entered. If you have been issued a KTN and are attempting to renew, please confirm you've entered the correct information and try again. You can contact customer service at CLEARCustomerSupport@tsa.dhs.gov for additional assistance or begin a new enrollment."
        successButtonText="Enroll"
        canClose={true}
        successClick={onClickEnroll}
      />
    </>
  )
}

export default KtnLookupPage
